/**
 * DV-6559 — Reenvío del postMessage de registro hacia la web que nos embebe
 * (join.vibia.com, la landing de campañas donde está instalado el GTM).
 *
 * El postMessage de fr-auth (`vibia.auth.signUp`) solo sube UN nivel: llega a
 * app.vibia.com (nuestro parent directo) y muere ahí. Cuando join.vibia.com nos
 * embebe — p.ej. la vista `/product-info-download` dentro de un modal de ficha de
 * producto — su GTM nunca ve el registro. Este módulo decide si un mensaje debe
 * reenviarse un nivel más arriba y con qué contenido.
 *
 * Reglas:
 *  - Solo mensajes que vengan de auth.* (directo) o de nuestro mismo origen
 *    (reenvío de un iframe hijo/hermano app.* → caso anidado join → app → app → auth).
 *  - Se reenvía un envelope SANEADO: únicamente los tres identificadores que GTM
 *    necesita. Nada de credenciales, ubigeo, profesión ni el resto del formulario.
 *  - `targets` incluye el mismo origen para que el salto intermedio app.* → app.*
 *    funcione; los targetOrigin que no coincidan con el parent real simplemente no
 *    se entregan (comportamiento estándar de postMessage).
 *
 * Módulo puro: no toca `window`. La decisión se testea en aislamiento y el hook
 * `useSignupForward` es quien aporta el contexto del navegador.
 */

export const SIGNUP_MESSAGE_ID = 'vibia.auth.signUp'

/**
 * Identificadores first-party del registro. Prioriza los campos explícitos que
 * fr-auth añade (DV-6559) y cae a los internos (`email`/`userId`) para no depender
 * del orden de despliegue de los repos.
 */
export const extractSignupIdentifiers = (payload) => {
	if (!payload || typeof payload !== 'object') return null

	const email = payload.user_email || payload.email
	const rawId = payload.external_id ?? payload.userId
	if (!email || rawId === undefined || rawId === null || rawId === '') return null

	const externalId = String(rawId)
	return {
		user_email: email,
		external_id: externalId,
		event_id: payload.event_id || `signup_${externalId}`
	}
}

export const buildSignupForward = ({ data, origin, selfOrigin, authOrigin, landingOrigin, isEmbedded }) => {
	if (!isEmbedded) return null
	if (origin !== authOrigin && origin !== selfOrigin) return null
	if (!data || data.id !== SIGNUP_MESSAGE_ID) return null

	const identifiers = extractSignupIdentifiers(data.payload)
	if (!identifiers) return null

	const targets = [landingOrigin, selfOrigin].filter((target, index, all) => target && all.indexOf(target) === index)
	if (!targets.length) return null

	return {
		envelope: { id: SIGNUP_MESSAGE_ID, payload: identifiers },
		targets
	}
}
